
import React, { useState, useRef } from 'react';
import { Job, Crew, JobStatus, Tree, TreeHealth, LineItem, PropertyDocument, DroneMission, JobType } from '../types';
import { analyzeTreeImage, analyzeDocument } from '../services/gemini';
import { Icons } from '../constants';
import { apiUrl, authFetch } from '../api';
import QuickAddJobModal from './QuickAddJobModal';
import WorkOrderModal from './WorkOrderModal';
import MapComponent from './MapComponent';
import { WorkOrder as WorkOrderWorksheet } from './worksheets/WorkOrder';

interface Props {
  jobs: Job[];
  missions: DroneMission[];
  crews: Crew[];
  onUpdate: (job: Job) => void;
  onRequestDrone: (jobId: string) => void;
  onAddJob?: (job: Job) => void;
  onDeleteJob?: (jobId: string) => Promise<{ ok: boolean; error?: string } | void> | void;
}

/**
 * Calendar day key in LOCAL time. `new Date("YYYY-MM-DD")` parses a date-only string as UTC
 * midnight, which lands on the previous day west of UTC — a job quick-added for today would be
 * filed under yesterday. Comparing on a local key avoids that.
 */
const localDayKey = (value?: string | Date | null): string => {
  if (!value) return '';
  const d = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? new Date(`${value}T00:00:00`)
    : new Date(value as any);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const CalendarView = ({ jobs, missions, onSelectJob }: { jobs: Job[], missions: DroneMission[], onSelectJob: (id: string) => void }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  
  const startOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
  const endOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
  
  const startDate = new Date(startOfMonth);
  startDate.setDate(startDate.getDate() - startDate.getDay()); // Start from Sunday
  
  const endDate = new Date(endOfMonth);
  if (endDate.getDay() !== 6) {
    endDate.setDate(endDate.getDate() + (6 - endDate.getDay())); // End on Saturday
  }
  
  const days = [];
  let day = new Date(startDate);
  while (day <= endDate) {
    days.push(new Date(day));
    day.setDate(day.getDate() + 1);
  }
  
  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  
  return (
    <div className="flex flex-col h-full bg-white rounded-2xl border border-stone-200 shadow-sm p-6 overflow-hidden">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-xl font-bold text-bark">
          {currentDate.toLocaleString('default', { month: 'long', year: 'numeric' })}
        </h3>
        <div className="flex space-x-2">
          <button onClick={prevMonth} className="p-2 hover:bg-stone-100 rounded-lg">&lt;</button>
          <button onClick={() => setCurrentDate(new Date())} className="px-4 py-2 hover:bg-stone-100 rounded-lg font-bold text-sm">Today</button>
          <button onClick={nextMonth} className="p-2 hover:bg-stone-100 rounded-lg">&gt;</button>
        </div>
      </div>
      
      <div className="grid grid-cols-7 gap-px bg-stone-200 rounded-lg overflow-hidden flex-1">
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => (
          <div key={d} className="bg-stone-50 p-2 text-center text-xs font-bold text-stone-500 uppercase">
            {d}
          </div>
        ))}
        {days.map((d, i) => {
          const isCurrentMonth = d.getMonth() === currentDate.getMonth();
          const isToday = d.toDateString() === new Date().toDateString();
          const dayJobs = jobs.filter(j => j.scheduledDate && localDayKey(j.scheduledDate) === localDayKey(d));
          const dayMissions = missions.filter(m => m.scheduledDate && localDayKey(m.scheduledDate) === localDayKey(d));
          
          return (
            <div key={i} className={`bg-white p-2 min-h-[100px] flex flex-col ${!isCurrentMonth ? 'opacity-50' : ''}`}>
              <div className={`text-xs font-bold w-6 h-6 flex items-center justify-center rounded-full mb-1 ${isToday ? 'bg-gecko text-white' : 'text-stone-700'}`}>
                {d.getDate()}
              </div>
              <div className="space-y-1 overflow-y-auto flex-1 custom-scrollbar">
                {dayJobs.map(j => (
                  <div 
                    key={j.id} 
                    onClick={() => onSelectJob(j.id)}
                    className="text-[10px] p-1 rounded bg-leaf/20 text-palm font-bold truncate cursor-pointer hover:bg-leaf/40 transition"
                  >
                    {new Date(j.scheduledDate!).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {j.clientName}
                  </div>
                ))}
                {dayMissions.map(m => (
                  <div 
                    key={m.id} 
                    onClick={() => onSelectJob(m.id)}
                    className="text-[10px] p-1 rounded bg-sky-100 text-sky-700 font-bold truncate cursor-pointer hover:bg-sky-200 transition"
                  >
                    🚁 {new Date(m.scheduledDate!).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - {m.missionType}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const JobsView: React.FC<Props> = ({ jobs, missions, crews, onUpdate, onRequestDrone, onAddJob, onDeleteJob }) => {
  const [viewMode, setViewMode] = useState<'pipeline' | 'calendar'>('pipeline');
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showQuickAddModal, setShowQuickAddModal] = useState(false);
  const [showWorkOrderModal, setShowWorkOrderModal] = useState(false);
  const [showJSHAModal, setShowJSHAModal] = useState(false);
  const [statusToConfirm, setStatusToConfirm] = useState<JobStatus | null>(null);
  const [isProcessingDoc, setIsProcessingDoc] = useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = useState(false);
  const [invoiceDocUrl, setInvoiceDocUrl] = useState<string | null>(null);
  const [editingJob, setEditingJob] = useState<Job | null>(null);
  const [jobToDelete, setJobToDelete] = useState<Job | null>(null);
  const [deleteError, setDeleteError] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [showWorkOrderWorksheet, setShowWorkOrderWorksheet] = useState(false);
  const docInputRef = useRef<HTMLInputElement>(null);
  
  const [manualTree, setManualTree] = useState<Partial<Tree>>({
    species: '',
    diameter: 0,
    health: TreeHealth.GOOD,
    location: '',
    notes: ''
  });

  const allJobs = [
    ...jobs.map(j => ({ ...j, type: JobType.TREE_WORK })),
    ...missions.map(m => {
      const job = jobs.find(j => j.id === m.jobId);
      return {
        id: m.id,
        clientName: job?.clientName || 'Drone Mission',
        address: job?.address || 'Remote Site',
        status: m.status as unknown as JobStatus,
        totalPrice: m.price || 0,
        scheduledDate: m.scheduledDate,
        type: JobType.DRONE_MISSION,
        missionDetails: m,
        inventory: [],
        lineItems: [],
        safetyChecked: false,
        documents: []
      } as Job;
    })
  ];

  const activeJob = allJobs.find(j => j.id === selectedJobId);

  const requestDelete = (job: Job, e?: React.MouseEvent) => {
    e?.stopPropagation();
    if (job.type === JobType.DRONE_MISSION) {
      alert('This card is a drone mission, not a job. Manage it from Aerial Intelligence.');
      return;
    }
    setDeleteError('');
    setJobToDelete(job);
  };

  const confirmDelete = async () => {
    if (!jobToDelete || !onDeleteJob || isDeleting) return;
    setIsDeleting(true);
    setDeleteError('');
    try {
      const result = await onDeleteJob(jobToDelete.id);
      if (result && result.ok === false) {
        setDeleteError(result.error || 'Delete failed. Please try again.');
        return;
      }
      setSelectedJobId(null);
      setJobToDelete(null);
    } catch (e: any) {
      setDeleteError(e?.message || 'Delete failed. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleStatusChangeAttempt = (newStatus: JobStatus) => {
    if (!activeJob) return;
    const terminalStates = [JobStatus.COMPLETED, JobStatus.PAID, JobStatus.INVOICED];
    
    if (terminalStates.includes(newStatus)) {
      setStatusToConfirm(newStatus);
    } else {
      onUpdate({ ...activeJob, status: newStatus });
    }
  };

  const confirmStatusChange = () => {
    if (activeJob && statusToConfirm) {
      onUpdate({ ...activeJob, status: statusToConfirm });
      setStatusToConfirm(null);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0] || !activeJob) return;
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(',')[1];
      const analysis = await analyzeTreeImage(base64);
      if (analysis) {
        const newTree: Tree = {
          id: Math.random().toString(36).substr(2, 9),
          species: analysis.species || 'Unknown Species',
          diameter: 0,
          health: (analysis.health?.toUpperCase() as TreeHealth) || TreeHealth.GOOD,
          location: 'Property Grounds',
          notes: analysis.recommendation || ''
        };
        onUpdate({
          ...activeJob,
          inventory: [...activeJob.inventory, newTree]
        });
      }
    };
    reader.readAsDataURL(file);
  };

  const handleJobAttachmentUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.length || !activeJob) return;
    const files = Array.from(e.target.files);
    
    const newAttachments = await Promise.all(files.map(file => {
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });
    }));

    onUpdate({
      ...activeJob,
      attachments: [...(activeJob.attachments || []), ...newAttachments]
    });
  };

  const handleDocUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0] || !activeJob) return;
    setIsProcessingDoc(true);
    const file = e.target.files[0];
    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = (reader.result as string).split(',')[1];
      const analysis = await analyzeDocument(base64);
      if (analysis) {
        const newDoc: PropertyDocument = {
          id: Math.random().toString(36).substr(2, 9),
          title: analysis.title || 'New Document',
          type: (analysis.type as any) || 'Other',
          summary: analysis.summary || '',
          dateAdded: new Date().toISOString(),
          url: reader.result as string
        };
        onUpdate({
          ...activeJob,
          documents: [...(activeJob.documents || []), newDoc]
        });
      }
      setIsProcessingDoc(false);
    };
    reader.readAsDataURL(file);
  };

  const handleManualAddSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeJob) return;

    const newTree: Tree = {
      id: Math.random().toString(36).substr(2, 9),
      species: manualTree.species || 'Unknown',
      diameter: manualTree.diameter || 0,
      health: manualTree.health || TreeHealth.GOOD,
      location: manualTree.location || 'Site',
      notes: manualTree.notes || '',
    };

    onUpdate({
      ...activeJob,
      inventory: [...activeJob.inventory, newTree]
    });

    setShowManualModal(false);
    setManualTree({ species: '', diameter: 0, health: TreeHealth.GOOD, location: '', notes: '' });
  };

  const handleJSHASubmit = (jsha: any) => {
    if (!activeJob) return;
    onUpdate({
      ...activeJob,
      jsha,
      safetyChecked: true
    });
    setShowJSHAModal(false);
  };

  const addLineItem = () => {
    if (!activeJob) return;
    const newItem: LineItem = { id: Date.now().toString(), description: 'New Task', quantity: 1, unitPrice: 0 };
    onUpdate({ ...activeJob, lineItems: [...activeJob.lineItems, newItem] });
  };

  const handleJobEditSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingJob) return;
    onUpdate(editingJob);
    setEditingJob(null);
  };

  return (
    <div className="flex flex-col space-y-4 h-full">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-bark">Jobs Management</h2>
        <div className="flex items-center space-x-4">
          {onAddJob && (
            <button 
              onClick={() => setShowQuickAddModal(true)}
              className="px-4 py-2 bg-palm text-white rounded-lg font-bold text-sm hover:bg-gecko transition shadow-sm flex items-center space-x-2"
            >
              <Icons.Lead />
              <span>Quick Add Job</span>
            </button>
          )}
          <div className="flex bg-stone-100 p-1 rounded-lg">
            <button 
              onClick={() => setViewMode('pipeline')}
              className={`px-4 py-2 rounded-md text-sm font-bold transition ${viewMode === 'pipeline' ? 'bg-white shadow-sm text-gecko' : 'text-stone-500 hover:text-stone-700'}`}
            >
              Pipeline
            </button>
            <button 
              onClick={() => setViewMode('calendar')}
              className={`px-4 py-2 rounded-md text-sm font-bold transition ${viewMode === 'calendar' ? 'bg-white shadow-sm text-gecko' : 'text-stone-500 hover:text-stone-700'}`}
            >
              Calendar
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'calendar' ? (
        <div className="flex-1 overflow-hidden">
          <CalendarView jobs={jobs} missions={missions} onSelectJob={(id) => { setSelectedJobId(id); setViewMode('pipeline'); }} />
        </div>
      ) : (
        <div className="flex space-x-6 flex-1 overflow-hidden">
          <div className="w-1/3 space-y-4 overflow-y-auto custom-scrollbar pr-2">
            <h3 className="font-bold text-lg mb-4">Pipeline</h3>
            {allJobs.length === 0 && <p className="text-stone-400 italic">No active jobs in pipeline.</p>}
            {allJobs.map(job => (
              <div 
                key={job.id} 
                onClick={() => setSelectedJobId(job.id)}
                className={`group p-4 rounded-xl border cursor-pointer transition-all ${
                  selectedJobId === job.id ? 'border-gecko bg-white shadow-lg' : 'border-stone-200 bg-white/50 hover:bg-white'
                } ${job.type === JobType.DRONE_MISSION ? 'border-l-4 border-l-sky-400' : ''}`}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center space-x-2">
                    <span className="text-[10px] font-bold uppercase text-stone-400">{job.status}</span>
                    {job.type === JobType.DRONE_MISSION && (
                      <span className="text-[10px] bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded font-bold">DRONE</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <span className="text-sm font-bold text-palm">${job.totalPrice}</span>
                    {job.type !== JobType.DRONE_MISSION && (
                      <button
                        type="button"
                        onClick={(e) => requestDelete(job, e)}
                        title="Delete job"
                        aria-label={`Delete ${job.clientName}`}
                        className="p-1.5 rounded-lg text-stone-300 hover:text-red-600 hover:bg-red-50 transition"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
                <p className="font-bold">{job.clientName}</p>
                <p className="text-xs text-stone-500 truncate">{job.address}</p>
                {job.scheduledDate && (
                  <p className="text-[10px] text-gecko font-bold mt-2 flex items-center">
                    <span className="mr-1">📅</span>
                    {new Date(job.scheduledDate).toLocaleString([], {month: 'short', day: 'numeric', hour: '2-digit', minute:'2-digit'})}
                  </p>
                )}
              </div>
            ))}
          </div>

          <div className="flex-1 bg-white rounded-2xl border border-stone-200 shadow-sm p-8 overflow-y-auto custom-scrollbar">
            {activeJob ? (
              <div className="space-y-8">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-2xl font-bold text-palm">{activeJob.clientName}</h2>
                <p className="text-stone-500">{activeJob.address}</p>
              </div>
              <div className="flex flex-col items-end space-y-2">
                <select 
                  className="bg-stone-100 border-none rounded-lg font-bold text-sm px-4 py-2"
                  value={activeJob.status}
                  onChange={e => handleStatusChangeAttempt(e.target.value as JobStatus)}
                >
                  {Object.values(JobStatus).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
                {activeJob.status === JobStatus.ESTIMATING && (
                  <button
                    onClick={() => handleStatusChangeAttempt(JobStatus.PENDING)}
                    className="text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 hover:bg-amber-200 transition"
                    title="Customer is waiting to schedule or approve this estimate"
                  >
                    Mark Estimate Pending
                  </button>
                )}
                {activeJob.status === JobStatus.PENDING && (
                  <button
                    onClick={() => handleStatusChangeAttempt(JobStatus.SCHEDULED)}
                    className="text-[10px] font-black uppercase tracking-wider px-3 py-1.5 rounded-full bg-gecko/10 text-gecko hover:bg-gecko/20 transition"
                    title="Customer approved; move to scheduling"
                  >
                    Mark Approved / Schedule
                  </button>
                )}
                <div className="flex space-x-2">
                  <button 
                    onClick={() => setEditingJob({ ...activeJob })}
                    className="text-xs font-bold px-3 py-1 rounded-full flex items-center transition bg-stone-100 text-stone-600 hover:bg-stone-200"
                    title="Edit job details"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                    Edit
                  </button>
                  {activeJob.type !== JobType.DRONE_MISSION && (
                    <button 
                      onClick={(e) => requestDelete(activeJob, e)}
                      className="text-xs font-bold px-3 py-1 rounded-full flex items-center transition bg-red-50 text-red-600 hover:bg-red-100"
                      title="Delete job"
                    >
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      Delete
                    </button>
                  )}
                  <button 
                    onClick={() => setShowWorkOrderWorksheet(true)}
                    className="text-xs font-bold px-3 py-1 rounded-full flex items-center transition bg-palm/10 text-palm hover:bg-palm/20"
                    title="Open the Work Order worksheet (field SOP)"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>
                    Work Order Worksheet
                  </button>
                  <button 
                    onClick={() => setShowJSHAModal(true)}
                    className={`text-xs font-bold px-3 py-1 rounded-full flex items-center transition ${activeJob.safetyChecked ? 'bg-gecko text-white' : 'bg-orange-100 text-orange-600 hover:bg-orange-200'}`}
                  >
                    <Icons.Safety />
                    <span className="ml-1">{activeJob.safetyChecked ? 'Safety Verified' : 'Safety Check Required'}</span>
                  </button>
                  <button 
                    onClick={() => setShowWorkOrderModal(true)}
                    className="text-xs font-bold text-palm bg-stone-100 px-3 py-1 rounded-full flex items-center hover:bg-stone-200 transition"
                  >
                    <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                    Documents
                  </button>
                  <button 
                    onClick={() => onRequestDrone(activeJob.id)}
                    className="text-xs font-bold text-gecko bg-leaf/10 px-3 py-1 rounded-full flex items-center hover:bg-leaf/20 transition"
                  >
                    <span className="mr-1"><Icons.Drone /></span>
                    Drone Survey
                  </button>
                </div>
              </div>
            </div>

            <section className="bg-stone-50 p-6 rounded-2xl border border-stone-200">
              <h4 className="text-sm font-bold uppercase tracking-widest text-stone-400 mb-4 flex items-center">
                <span className="mr-2">📍</span> Location Details
              </h4>
              <MapComponent address={activeJob.address} title={activeJob.clientName} />
            </section>

            <section>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-bold uppercase tracking-widest text-stone-400">Scheduling & Assignment</h4>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Scheduled Date & Time</label>
                  <input 
                    type="datetime-local" 
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none"
                    value={activeJob.scheduledDate ? new Date(new Date(activeJob.scheduledDate).getTime() - new Date().getTimezoneOffset() * 60000).toISOString().slice(0, 16) : ''}
                    onChange={e => {
                      const newDate = e.target.value ? new Date(e.target.value).toISOString() : undefined;
                      onUpdate({ ...activeJob, scheduledDate: newDate });
                    }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Assigned Crew</label>
                  <select 
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none"
                    value={activeJob.crewId || ''}
                    onChange={e => onUpdate({ ...activeJob, crewId: e.target.value || undefined })}
                  >
                    <option value="">Unassigned</option>
                    {crews.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </select>
                </div>
              </div>
            </section>

            <section>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-bold uppercase tracking-widest text-stone-400">Attachments & Diagrams</h4>
                <label className="text-sm font-bold text-palm cursor-pointer hover:underline">
                  + Upload Photo/Diagram
                  <input type="file" className="hidden" multiple accept="image/*" onChange={handleJobAttachmentUpload} />
                </label>
              </div>
              {activeJob.attachments && activeJob.attachments.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {activeJob.attachments.map((src, idx) => (
                    <div key={idx} className="relative group rounded-xl overflow-hidden border border-stone-200">
                      <img src={src} alt={`Attachment ${idx + 1}`} className="w-full h-32 object-cover" />
                      <a 
                        href={src} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="absolute inset-0 bg-bark/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <span className="text-white font-bold text-sm">View Full</span>
                      </a>
                      <button 
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          onUpdate({
                            ...activeJob,
                            attachments: activeJob.attachments?.filter((_, i) => i !== idx)
                          });
                        }}
                        className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-8 border-2 border-dashed border-stone-100 rounded-xl text-center text-stone-400">
                  No attachments or diagrams provided for this job.
                </div>
              )}
            </section>

            {activeJob.safetyChecked && activeJob.jsha && (
              <section className="bg-leaf/5 p-6 rounded-2xl border border-gecko/20">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-sm font-bold uppercase tracking-widest text-gecko">Safety Briefing (JSHA)</h4>
                  <span className="text-[10px] font-bold text-stone-400">Assessed by {activeJob.jsha.assessedBy} on {new Date(activeJob.jsha.date).toLocaleDateString()}</span>
                </div>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2">Identified Hazards</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(activeJob.jsha.hazards).map(([key, val]) => val && key !== 'other' && (
                        <span key={key} className="px-2 py-1 bg-white border border-stone-200 rounded text-[10px] font-bold text-stone-600 uppercase">{key}</span>
                      ))}
                      {activeJob.jsha.hazards.other && (
                        <span className="px-2 py-1 bg-white border border-stone-200 rounded text-[10px] font-bold text-stone-600 uppercase">Other: {activeJob.jsha.hazards.other}</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-stone-400 uppercase tracking-widest mb-2">Emergency Plan</p>
                    <div className="text-xs space-y-1">
                      <p><span className="text-stone-500">Hospital:</span> <span className="font-bold">{activeJob.jsha.emergencyPlan.nearestHospital}</span></p>
                      <p><span className="text-stone-500">Muster Point:</span> <span className="font-bold">{activeJob.jsha.emergencyPlan.musterPoint}</span></p>
                    </div>
                  </div>
                </div>
              </section>
            )}

            <section>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-bold uppercase tracking-widest text-stone-400">Tree Inventory</h4>
                <div className="flex items-center space-x-4">
                  <button 
                    onClick={() => setShowManualModal(true)}
                    className="text-sm font-bold text-gecko hover:underline"
                  >
                    + Add Tree Manually
                  </button>
                  <label className="text-sm font-bold text-palm cursor-pointer hover:underline">
                    + Upload Photo (AI ID)
                    <input type="file" className="hidden" accept="image/*" onChange={handleFileUpload} />
                  </label>
                </div>
              </div>

              {activeJob.inventory.length === 0 ? (
                <div className="p-8 border-2 border-dashed border-stone-100 rounded-xl text-center text-stone-400">
                  No trees inventoried yet. Use AI camera or add manually.
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {activeJob.inventory.map(tree => (
                    <div key={tree.id} className="p-4 bg-stone-50 rounded-xl border border-stone-200">
                      <div className="flex justify-between">
                        <span className="font-bold">{tree.species}</span>
                        <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                          tree.health === TreeHealth.EXCELLENT ? 'bg-green-100 text-green-700' : 
                          tree.health === TreeHealth.GOOD ? 'bg-blue-100 text-blue-700' :
                          tree.health === TreeHealth.POOR || tree.health === TreeHealth.CRITICAL ? 'bg-red-100 text-red-700' : 'bg-orange-100 text-orange-700'
                        }`}>{tree.health}</span>
                      </div>
                      <div className="mt-2 text-xs text-stone-600 flex space-x-3">
                        <span><strong>Size:</strong> {tree.diameter}" DBH</span>
                        <span><strong>Loc:</strong> {tree.location}</span>
                      </div>
                      <p className="text-xs text-stone-500 mt-2 italic">{tree.notes}</p>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-bold uppercase tracking-widest text-stone-400">Knowledge Base & Docs</h4>
                <div className="flex items-center space-x-4">
                  <input type="file" className="hidden" ref={docInputRef} accept="image/*,application/pdf" onChange={handleDocUpload} />
                  <button 
                    disabled={isProcessingDoc}
                    onClick={() => docInputRef.current?.click()}
                    className="text-sm font-bold text-palm hover:underline"
                  >
                    {isProcessingDoc ? 'Processing...' : '+ Scan Manual/Warranty'}
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                {activeJob.documents?.map(doc => (
                  <div key={doc.id} className="p-4 bg-stone-50 rounded-xl border border-stone-100">
                    <div className="flex justify-between items-start">
                      <h5 className="font-bold text-bark">{doc.title}</h5>
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded bg-bark/10 text-bark uppercase">{doc.type}</span>
                    </div>
                    {doc.url && (
                      <img src={doc.url} alt={doc.title} className="mt-2 w-full max-h-48 object-cover rounded-lg" referrerPolicy="no-referrer" />
                    )}
                    <p className="text-xs text-stone-500 mt-2">{doc.summary}</p>
                    <p className="text-[10px] text-stone-400 mt-2 uppercase font-bold">Added {new Date(doc.dateAdded).toLocaleDateString()}</p>
                  </div>
                ))}
                {(!activeJob.documents || activeJob.documents.length === 0) && (
                  <p className="text-xs text-stone-400 italic">No manuals or warranties attached to this property.</p>
                )}
              </div>
            </section>

            <section>
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-bold uppercase tracking-widest text-stone-400">Line Items</h4>
                <button onClick={addLineItem} className="text-sm font-bold text-palm hover:underline">+ Add Item</button>
              </div>
              <div className="space-y-2">
                {activeJob.lineItems.map(item => (
                  <div key={item.id} className="flex space-x-4 items-center bg-stone-50 p-3 rounded-lg">
                    <input 
                      className="flex-1 bg-transparent border-none font-medium" 
                      defaultValue={item.description} 
                      onChange={e => {
                        const description = e.target.value;
                        onUpdate({
                          ...activeJob,
                          lineItems: activeJob.lineItems.map(li => li.id === item.id ? {...li, description} : li)
                        });
                      }}
                    />
                    <input 
                      className="w-16 bg-transparent border-none text-right" 
                      defaultValue={item.quantity} 
                      onChange={e => {
                        const quantity = parseFloat(e.target.value) || 0;
                        onUpdate({
                          ...activeJob,
                          lineItems: activeJob.lineItems.map(li => li.id === item.id ? {...li, quantity} : li),
                          totalPrice: activeJob.lineItems.reduce((acc, li) => {
                            const q = li.id === item.id ? quantity : (Number(li.quantity) || 0);
                            const p = Number(li.unitPrice) || 0;
                            return acc + (q * p);
                          }, 0)
                        });
                      }}
                    />
                    <input 
                      className="w-24 bg-transparent border-none text-right font-bold" 
                      defaultValue={item.unitPrice} 
                      onChange={e => {
                        const price = parseFloat(e.target.value) || 0;
                        onUpdate({
                          ...activeJob,
                          lineItems: activeJob.lineItems.map(li => li.id === item.id ? {...li, unitPrice: price} : li),
                          totalPrice: activeJob.lineItems.reduce((acc, li) => {
                            const q = Number(li.quantity) || 0;
                            const p = li.id === item.id ? price : (Number(li.unitPrice) || 0);
                            return acc + (q * p);
                          }, 0)
                        });
                      }}
                    />
                  </div>
                ))}
                <div className="flex justify-end pt-4 border-t border-stone-100">
                  <p className="text-lg font-bold">Total: ${activeJob.totalPrice}</p>
                </div>
              </div>
            </section>

            {/* Post-Completion Workflow */}
            {(activeJob.status === JobStatus.COMPLETED || activeJob.status === JobStatus.INVOICED || activeJob.status === JobStatus.PAID) && (
              <section className="pt-6 border-t border-stone-200">
                <h4 className="text-sm font-bold uppercase tracking-widest text-stone-400 mb-4">Post-Completion Workflow</h4>
                <div className="flex flex-col gap-3">
                  <div className="flex flex-wrap gap-3">
                    <button 
                      onClick={() => handleStatusChangeAttempt(JobStatus.INVOICED)}
                      disabled={activeJob.status === JobStatus.INVOICED || activeJob.status === JobStatus.PAID}
                      className={`px-4 py-2 rounded-xl font-bold text-sm transition ${activeJob.status === JobStatus.INVOICED || activeJob.status === JobStatus.PAID ? 'bg-gecko text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200'}`}
                    >
                      1. Mark as Invoiced
                    </button>
                    <button
                      onClick={async () => {
                        setIsGeneratingInvoice(true);
                        try {
                          const res = await authFetch('/api/workspace/invoice/generate', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({
                              jobId: activeJob.id,
                              clientName: activeJob.clientName,
                              address: activeJob.address,
                              lineItems: activeJob.lineItems,
                              totalPrice: activeJob.totalPrice,
                              userEmail: 'mrgreen@greentreearboriculture.com' // Should be dynamic in a real scenario
                            })
                          });
                          const data = await res.json();
                          if (data.success && data.url) {
                            setInvoiceDocUrl(data.url);
                          } else {
                            alert('Failed to generate invoice: ' + (data.error || 'Unknown error'));
                          }
                        } catch (err) {
                          console.error(err);
                          alert('Error generating invoice');
                        } finally {
                          setIsGeneratingInvoice(false);
                        }
                      }}
                      disabled={isGeneratingInvoice}
                      className="px-4 py-2 rounded-xl font-bold text-sm transition bg-[#4285F4] text-white hover:bg-[#3367D6] flex items-center shadow-sm"
                    >
                      {isGeneratingInvoice ? 'Generating Doc...' : 'Generate Google Doc Invoice'}
                    </button>
                    <button 
                      onClick={() => handleStatusChangeAttempt(JobStatus.PAID)}
                      disabled={activeJob.status !== JobStatus.INVOICED && activeJob.status !== JobStatus.PAID}
                      className={`px-4 py-2 rounded-xl font-bold text-sm transition ${activeJob.status === JobStatus.PAID ? 'bg-gecko text-white' : 'bg-stone-100 text-stone-500 hover:bg-stone-200 disabled:opacity-50'}`}
                    >
                      2. Mark Paid
                    </button>
                  </div>
                  {invoiceDocUrl && (
                    <div className="p-4 bg-blue-50 border border-blue-100 rounded-lg w-full">
                      <p className="text-sm text-blue-800">
                        Invoice successfully generated in Google Docs!
                      </p>
                      <a href={invoiceDocUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm font-bold mt-2 inline-block">
                        Open Google Doc Invoice ↗
                      </a>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        ) : (
          <div className="h-full flex flex-col items-center justify-center text-stone-400">
            <Icons.Tree />
            <p className="mt-4 font-medium">Select a job from the pipeline to manage details</p>
          </div>
        )}
      </div>
        </div>
      )}

      {/* Manual Tree Modal */}
      {showManualModal && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="bg-palm p-6 text-white">
              <h3 className="text-xl font-bold">Add Tree to Inventory</h3>
              <p className="text-palm-100 text-sm opacity-80 italic">Enter precise details for estimate accuracy.</p>
            </div>
            <form onSubmit={handleManualAddSubmit} className="p-8 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Species</label>
                  <input 
                    required 
                    type="text" 
                    placeholder="e.g. White Oak"
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none"
                    value={manualTree.species}
                    onChange={e => setManualTree({...manualTree, species: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Diameter (DBH in)</label>
                  <input 
                    required 
                    type="number" 
                    placeholder="0"
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none"
                    value={manualTree.diameter}
                    onChange={e => setManualTree({...manualTree, diameter: parseInt(e.target.value) || 0})}
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Health Status</label>
                <select 
                  className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none"
                  value={manualTree.health}
                  onChange={e => setManualTree({...manualTree, health: e.target.value as TreeHealth})}
                >
                  {Object.values(TreeHealth).map(h => (
                    <option key={h} value={h}>{h}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Location on Property</label>
                <input 
                  required 
                  type="text" 
                  placeholder="e.g. Back yard near fence"
                  className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none"
                  value={manualTree.location}
                  onChange={e => setManualTree({...manualTree, location: e.target.value})}
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Arborist Notes</label>
                <textarea 
                  rows={3}
                  placeholder="Note specific hazards or structural defects..."
                  className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none resize-none"
                  value={manualTree.notes}
                  onChange={e => setManualTree({...manualTree, notes: e.target.value})}
                />
              </div>
              <div className="flex space-x-3 pt-4">
                <button type="button" onClick={() => setShowManualModal(false)} className="flex-1 py-3 text-stone-500 font-bold hover:bg-stone-100 rounded-lg transition">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-gecko text-white font-bold rounded-lg hover:bg-palm transition shadow-lg shadow-gecko/20">Save Tree</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Job Modal */}
      {editingJob && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden">
            <div className="bg-palm p-6 text-white">
              <h3 className="text-xl font-bold">Edit Job</h3>
              <p className="text-palm-100 text-sm opacity-80 italic">Update the core job details.</p>
            </div>
            <form onSubmit={handleJobEditSubmit} className="p-8 space-y-4">
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Client Name</label>
                <input required type="text" value={editingJob.clientName} onChange={e => setEditingJob({ ...editingJob, clientName: e.target.value })}
                  className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Email</label>
                  <input type="email" value={editingJob.email} onChange={e => setEditingJob({ ...editingJob, email: e.target.value })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Phone</label>
                  <input type="text" value={editingJob.phone} onChange={e => setEditingJob({ ...editingJob, phone: e.target.value })}
                    className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Address</label>
                <input type="text" value={editingJob.address} onChange={e => setEditingJob({ ...editingJob, address: e.target.value })}
                  className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none" />
              </div>
              <div>
                <label className="block text-xs font-bold text-stone-500 uppercase mb-1">Service Type</label>
                <select value={editingJob.serviceType} onChange={e => setEditingJob({ ...editingJob, serviceType: e.target.value as any })}
                  className="w-full p-3 bg-stone-50 rounded-lg border border-stone-200 focus:ring-2 focus:ring-gecko outline-none">
                  {['Removal', 'Pruning', 'Plant Health', 'Emergency', 'Consulting'].map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="flex space-x-3 pt-2">
                <button type="button" onClick={() => setEditingJob(null)} className="flex-1 py-3 text-stone-500 font-bold hover:bg-stone-100 rounded-lg transition">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-gecko text-white font-bold rounded-lg hover:bg-palm transition shadow-lg shadow-gecko/20">Save Changes</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Job Modal */}
      {showWorkOrderWorksheet && (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm z-[120] overflow-y-auto p-4">
          <div className="max-w-4xl mx-auto my-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-black text-white">Work Order Worksheet</h3>
              <button
                onClick={() => setShowWorkOrderWorksheet(false)}
                className="px-4 py-2 bg-white rounded-xl font-bold text-sm text-bark hover:bg-stone-100 transition"
              >
                Close
              </button>
            </div>
            <div className="bg-brand-cream rounded-2xl p-4 sm:p-6">
              <WorkOrderWorksheet />
            </div>
          </div>
        </div>
      )}

      {jobToDelete && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
            </div>
            <h3 className="text-2xl font-bold text-bark mb-2">Delete this job?</h3>
            <p className="text-stone-500 mb-8 leading-relaxed">This soft-deletes <span className="font-bold text-bark">{jobToDelete.clientName}</span> from the pipeline. Linked invoices stay in Financials for accounting, and the deletion is recorded in the change log.</p>
            {deleteError && (
              <p role="alert" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2 mb-4 text-left">
                {deleteError}
              </p>
            )}
            <div className="flex space-x-3">
              <button
                onClick={() => { setJobToDelete(null); setDeleteError(''); }}
                disabled={isDeleting}
                className="flex-1 py-3 border-2 border-stone-100 text-stone-500 font-bold rounded-xl hover:bg-stone-50 transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={isDeleting}
                className="flex-1 py-3 bg-red-500 text-white font-bold rounded-xl hover:bg-red-600 transition disabled:opacity-60"
              >
                {isDeleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Modal */}
      {statusToConfirm && (
        <div className="fixed inset-0 bg-bark/60 backdrop-blur-sm flex items-center justify-center z-[110] p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl p-8 text-center">
            <div className="w-16 h-16 bg-orange-100 text-orange-600 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-2xl font-bold text-bark mb-2">Are you sure?</h3>
            <p className="text-stone-500 mb-8 leading-relaxed">You are about to move this job to <span className="font-bold text-palm">{statusToConfirm}</span>. This may trigger automated invoices, payroll updates, or notify the client.</p>
            <div className="flex space-x-3">
              <button onClick={() => setStatusToConfirm(null)} className="flex-1 py-3 border-2 border-stone-100 text-stone-500 font-bold rounded-xl hover:bg-stone-50 transition">Go Back</button>
              <button onClick={confirmStatusChange} className="flex-1 py-3 bg-palm text-white font-bold rounded-xl hover:bg-bark transition shadow-lg shadow-palm/20">Confirm Update</button>
            </div>
          </div>
        </div>
      )}

      {showQuickAddModal && onAddJob && (
        <QuickAddJobModal
          onClose={() => setShowQuickAddModal(false)}
          onAddJob={(job) => {
            onAddJob(job);
            setShowQuickAddModal(false);
          }}
        />
      )}

      {showWorkOrderModal && activeJob && (
        <WorkOrderModal 
          job={activeJob} 
          onClose={() => setShowWorkOrderModal(false)} 
        />
      )}

      {showJSHAModal && activeJob && (
        <div className="fixed inset-0 bg-bark/70 backdrop-blur-md flex items-center justify-center z-[150] p-4 overflow-y-auto">
          <div className="bg-white rounded-[40px] w-full max-w-2xl shadow-2xl overflow-hidden my-8">
            <div className="bg-orange-600 p-8 text-white relative">
              <div className="relative z-10">
                <h3 className="text-2xl font-bold">Job Site Hazard Assessment</h3>
                <p className="text-orange-100 text-sm opacity-80">Safety is our #1 priority. Complete this assessment before work begins.</p>
              </div>
              <div className="absolute right-0 top-0 p-8 opacity-10">
                <div className="w-16 h-16"><Icons.Safety /></div>
              </div>
            </div>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const jsha = {
                id: Date.now().toString(),
                date: new Date().toISOString(),
                assessedBy: formData.get('assessedBy'),
                hazards: {
                  overhead: formData.get('overhead') === 'on',
                  underground: formData.get('underground') === 'on',
                  traffic: formData.get('traffic') === 'on',
                  weather: formData.get('weather') === 'on',
                  wildlife: formData.get('wildlife') === 'on',
                  publicAccess: formData.get('publicAccess') === 'on',
                  steepTerrain: formData.get('steepTerrain') === 'on',
                  other: formData.get('otherHazard')
                },
                ppe: {
                  helmet: formData.get('helmet') === 'on',
                  eyeProtection: formData.get('eyeProtection') === 'on',
                  earProtection: formData.get('earProtection') === 'on',
                  chainsawChaps: formData.get('chainsawChaps') === 'on',
                  highViz: formData.get('highViz') === 'on',
                  gloves: formData.get('gloves') === 'on',
                  climbingGear: formData.get('climbingGear') === 'on'
                },
                emergencyPlan: {
                  nearestHospital: formData.get('hospital'),
                  musterPoint: formData.get('musterPoint'),
                  firstAidKitLocation: formData.get('firstAidKit')
                },
                completed: true
              };
              handleJSHASubmit(jsha);
            }} className="p-10 space-y-8">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Assessed By</label>
                  <input name="assessedBy" required type="text" className="w-full p-4 bg-stone-50 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-orange-500 outline-none font-bold" placeholder="Crew Leader Name" />
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Hospital Info</label>
                  <input name="hospital" required type="text" className="w-full p-4 bg-stone-50 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-orange-500 outline-none font-bold" placeholder="Nearest Hospital" />
                </div>
              </div>

              <div>
                <h4 className="text-xs font-black text-stone-400 uppercase tracking-widest mb-4">Hazard Identification</h4>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {['Overhead Lines', 'Underground Utilities', 'Traffic', 'Weather Conditions', 'Wildlife/Insects', 'Public Access', 'Steep Terrain'].map(h => (
                    <label key={h} className="flex items-center space-x-3 p-3 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition">
                      <input type="checkbox" name={h.split(' ')[0].toLowerCase()} className="w-5 h-5 rounded border-stone-300 text-orange-600 focus:ring-orange-500" />
                      <span className="text-sm font-bold text-stone-700">{h}</span>
                    </label>
                  ))}
                </div>
                <input name="otherHazard" type="text" className="w-full mt-4 p-4 bg-stone-50 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-orange-500 outline-none font-medium text-sm" placeholder="Other hazards..." />
              </div>

              <div>
                <h4 className="text-xs font-black text-stone-400 uppercase tracking-widest mb-4">PPE Verification</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {['Helmet', 'Eye Protection', 'Ear Protection', 'Chainsaw Chaps', 'High Viz', 'Gloves', 'Climbing Gear'].map(p => (
                    <label key={p} className="flex items-center space-x-3 p-3 bg-stone-50 rounded-xl cursor-pointer hover:bg-stone-100 transition">
                      <input type="checkbox" name={p.split(' ')[0].toLowerCase()} className="w-5 h-5 rounded border-stone-300 text-orange-600 focus:ring-orange-500" />
                      <span className="text-sm font-bold text-stone-700">{p}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">Muster Point</label>
                  <input name="musterPoint" required type="text" className="w-full p-4 bg-stone-50 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-orange-500 outline-none font-bold" placeholder="Emergency Gathering Area" />
                </div>
                <div>
                  <label className="block text-xs font-black text-stone-400 uppercase tracking-widest mb-2">First Aid Kit Location</label>
                  <input name="firstAidKit" required type="text" className="w-full p-4 bg-stone-50 rounded-2xl border border-stone-200 focus:ring-2 focus:ring-orange-500 outline-none font-bold" placeholder="e.g. Truck 1 Cab" />
                </div>
              </div>

              <div className="flex space-x-4 pt-4">
                <button type="button" onClick={() => setShowJSHAModal(false)} className="flex-1 py-4 text-stone-400 font-bold hover:bg-stone-50 rounded-2xl transition">Cancel</button>
                <button type="submit" className="flex-1 py-4 bg-orange-600 text-white font-bold rounded-2xl hover:bg-orange-700 transition shadow-xl shadow-orange-600/20">Verify Safety & Save JSHA</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobsView;
